import React, { useState, useRef, useEffect } from 'react'
import { CloseOutlined } from '@ant-design/icons'
import styles from './index.module.scss'
import clsx from 'clsx'

interface DraggableModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  children: React.ReactNode
  initialWidth?: number
  initialHeight?: number
}

export const DraggableModal: React.FC<DraggableModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  initialWidth = 450,
  initialHeight = 350,
}) => {
  const [isInitialized, setIsInitialized] = useState(false)
  const [position, setPosition] = useState({ x: -9999, y: -9999 })
  const [size, setSize] = useState({ width: initialWidth, height: initialHeight })
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState<string | false>(false)

  const modalRef = useRef<HTMLDivElement>(null)
  const interactionStartRef = useRef({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    startX: 0,
    startY: 0,
  })

  useEffect(() => {
    if (isOpen && !isInitialized) {
      requestAnimationFrame(() => {
        if (modalRef.current) {
          const modalRect = modalRef.current.getBoundingClientRect()
          const viewportHeight = window.innerHeight

          const bottomLeftX = 24
          const bottomLeftY = viewportHeight - modalRect.height - 24

          setPosition({ x: bottomLeftX, y: bottomLeftY })
          setIsInitialized(true)
        }
      })
    } else if (!isOpen) {

      setIsInitialized(false)
      setPosition({ x: -9999, y: -9999 })
    }
  }, [isOpen, isInitialized])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  const handleDragStart = (e: React.MouseEvent) => {
    if (e.button !== 0) return
    setIsDragging(true)
    interactionStartRef.current = {
      ...interactionStartRef.current,
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    }
  }

  const handleResizeStart = (e: React.MouseEvent, direction: string) => {
    e.stopPropagation()
    if (e.button !== 0) return
    setIsResizing(direction)
    if (modalRef.current) {
      const rect = modalRef.current.getBoundingClientRect()
      interactionStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        width: rect.width,
        height: rect.height,
        startX: position.x,
        startY: position.y,
      }
    }
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        let newX = e.clientX - interactionStartRef.current.x
        let newY = e.clientY - interactionStartRef.current.y

        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight
        if (modalRef.current) {
          const modalWidth = modalRef.current.offsetWidth
          const modalHeight = modalRef.current.offsetHeight
          newX = Math.max(0, Math.min(newX, viewportWidth - modalWidth))
          newY = Math.max(0, Math.min(newY, viewportHeight - modalHeight))
        }

        setPosition({ x: newX, y: newY })
      } else if (isResizing) {
        const deltaX = e.clientX - interactionStartRef.current.x
        const deltaY = e.clientY - interactionStartRef.current.y
        const { width, height, startX, startY } = interactionStartRef.current
        let newWidth = width,
          newHeight = height,
          newX = startX,
          newY = startY

        if (isResizing.includes('right')) newWidth = Math.max(300, width + deltaX)
        if (isResizing.includes('bottom')) newHeight = Math.max(200, height + deltaY)
        if (isResizing.includes('left')) {
          newWidth = Math.max(300, width - deltaX)
          if (newWidth > 300) newX = startX + deltaX
        }
        if (isResizing.includes('top')) {
          newHeight = Math.max(200, height - deltaY)
          if (newHeight > 200) newY = startY + deltaY
        }

        setSize({ width: newWidth, height: newHeight })
        setPosition({ x: newX, y: newY })
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      setIsResizing(false)
      document.body.style.cursor = ''
    }

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = isResizing ? `${isResizing}-resize` : 'grabbing'
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, isResizing, position])

  if (!isOpen) return null

  return (
    <div
      ref={modalRef}
      className={clsx(styles.modal, { [styles.visible]: isInitialized })}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
      }}
    >
      <div className={clsx(styles.header, { [styles.dragging]: isDragging })} onMouseDown={handleDragStart}>
        <div className={styles.titleSection}>
          <h2 className={styles.title}>{title}</h2>
        </div>
        <button onClick={onClose} className={styles.closeButton} aria-label="Close">
          <CloseOutlined />
        </button>
      </div>

      <div className={styles.content}>{children}</div>

      {['top', 'bottom', 'left', 'right', 'top-left', 'top-right', 'bottom-left', 'bottom-right'].map((direction) => (
        <div
          key={direction}
          className={clsx(styles.resizeHandle, styles[direction])}
          onMouseDown={(e) => handleResizeStart(e, direction)}
        />
      ))}
    </div>
  )
}
